import React, { useEffect, useMemo } from 'react'
import { Redirect } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

// material ui
import { ThemeProvider } from '@mui/material/styles'
import {
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
// app routes
import AppWithSession from 'AppWithSession'
import { AgCharts as AgChartsEnterprise } from 'ag-charts-enterprise'
import { LicenseManager } from 'ag-grid-enterprise'

// Constants
import { GENERIC_ERROR_MESSAGE } from './constants/error-message'
import { DEFAULT_QUERY_RETRY_COUNT } from './constants/react-query.constant'
import redirects from './constants/redirects'

import { NavProvider } from './context/NavContext'

// helper
import confirm from './helpers/confirm'
import { decodeBase64 } from './helpers/encodeDecodeBase64'

import envConfig from './services/env-config'
import { toastService } from './services/toast.service'

import theme from './theme'
import './wydr'

const encodedKey = envConfig.ENCODED_AG_CHART_LICENSE_KEY
const plainKey = envConfig.AG_CHART_LICENSE_KEY

const licenseKey = plainKey || (encodedKey ? decodeBase64(encodedKey) : null)

if (licenseKey) {
    LicenseManager.setLicenseKey(licenseKey)
    AgChartsEnterprise.setLicenseKey(licenseKey)
} else {
    console.warn(
        'AG Charts license key is missing. Chart may not render correctly.',
    )
}

const environment = envConfig.ENVIRONMENT

const queryCache = new QueryCache({
    onError: (error, query) => {
        if (environment !== 'production' && !query.meta?.skipGlobalErrorToast) {
            toastService.showError(error.message ?? GENERIC_ERROR_MESSAGE)
        }
    },
})

export const queryClient = new QueryClient({
    queryCache,
    defaultOptions: {
        queries: {
            retry: DEFAULT_QUERY_RETRY_COUNT,
            refetchOnWindowFocus: false,
        },
    },
})

export default function App() {
    const getUserConfirmation = async (message, setConfirmation) => {
        const confirmed = await confirm(
            'Leave without saving your changes?',
            'warning',
            undefined,
            'No',
            'Yes',
        )
        setConfirmation(confirmed)
    }

    useEffect(() => {
        if (environment === 'staging') {
            document.title = '[STAGING] Aampe Composer'
        } else if (environment === 'development') {
            document.title = '[DEV] Aampe Composer'
        } else if (environment === 'local') {
            document.title = '[LOCAL] Aampe Composer'
        }
    }, [])

    const redirectedRoutes = useMemo(() => {
        return redirects.map((redirect, index) => (
            <Redirect key={index} from={redirect.from} to={redirect.to} exact />
        ))
    }, [])

    return (
        <ThemeProvider theme={theme}>
            <ToastContainer />
            <QueryClientProvider client={queryClient}>
                <NavProvider>
                    <AppWithSession
                        {...{ getUserConfirmation, redirectedRoutes }}
                    />
                </NavProvider>
                <ReactQueryDevtools />
            </QueryClientProvider>
        </ThemeProvider>
    )
}
